import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import styled from "styled-components";
import "./GantryLocate.css";

const Views = {
	TOP_VIEW: "TOP_VIEW",
	SIDE_VIEW: "SIDE_VIEW"
};

const GantryConfig = {
	MACHINE_DIMENSIONS: {
		x: 600,
		y: 220,
		z: 500,
	},
	FRONT: ["PPR","EPR", "MICROWAVE"],
	BACK: ["SINGLE_EPR","PPR", "EPR"],
};

const StyleClasses = {
	[Views.TOP_VIEW]: {
		PLAY_AREA : "top-view-play-area",
		FRONT: "top-view-front-row",
		BACK: "top-view-back-row",
		MACHINE: "top-view",
		PPR: "top-view-ppr",
		EPR: "top-view-epr",
		SINGLE_EPR: "top-view-single-epr",
		SINGLE_PPR: "top-view-single-ppr",
		MICROWAVE: "top-view-microwave",
		EMPTY:"top-view-empty-space",
		GANTRY: "top-view-gantry",
		GANTRY_BOX:"top-view-gantry-box",
		GANTRY_PICKER:"top-view-gantry-picker",
	},
	[Views.SIDE_VIEW] : {
		MACHINE:"side-view",
		PLAY_AREA: "side-view-play-area",
		GANTRY: "side-view-gantry",
		GANTRY_BOX:"side-view-gantry-box",
		GANTRY_PICKER:"side-view-gantry-picker",
	}
};

const GantrySuffix = "-gantry";

const Page = styled.div`
	height: 100%;
	width: 100%;
	max-width:max-content;
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 2rem;
	margin:0 0 2rem 0;
	box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 8px;
	border-radius:10px;
	background:#FFF;
	@media (max-width: 1000px) {
		padding: 0 2rem;
	}
`;

const Divider = styled.div`
	border-right:1px solid rgba(0,0,0,0.5);
	margin:0 1.5rem 0 0.5rem;
	height:100%;
`;

const Row = styled.div`
	display: flex;
	justify-content: space-around;
	align-items: center;
	width: 100%;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const TopViewGantry = () => {
	return (
		<div
			id={`${Views.TOP_VIEW}${GantrySuffix}`}
			className={StyleClasses.TOP_VIEW.GANTRY}>
				<div className={StyleClasses.TOP_VIEW.GANTRY_BOX}></div>
				<div className={StyleClasses.TOP_VIEW.GANTRY_PICKER}></div>
			</div>
	);
};

const SideViewGantry = () => {
	return (
		<div
			className={`${StyleClasses.SIDE_VIEW.GANTRY} ${StyleClasses.SIDE_VIEW.PLAY_AREA}`}>
				<div className={StyleClasses.SIDE_VIEW.GANTRY_BOX}></div>
				<div id={`${Views.SIDE_VIEW}${GantrySuffix}`} className={StyleClasses.SIDE_VIEW.GANTRY_PICKER}></div>
			</div>
	);
}

const GantryComponents = {
	[Views.TOP_VIEW]: <TopViewGantry />,
	[Views.SIDE_VIEW] : <SideViewGantry />
};

const BUFFER = {
	width:80,
	height:20,
}

const Z_BUFFER = 20

const convertToUiCoordinates = (newCoordinates, view, machineCoordinates, wrapperElement = null, buffer = BUFFER) => {

	let convertedCoordinates = {};
	const playArea = wrapperElement;
	if(!playArea) return {} ;
	const { height:playAreaHeight, width:playAreaWidth } = playArea.getBoundingClientRect()
	switch(view) {
		case Views.TOP_VIEW : 
			for (const axis in newCoordinates) {

				let axisCoordinate = newCoordinates[axis];
				//Only handle the axes which have known dimensions
				if (axis in machineCoordinates) {
					const max = machineCoordinates[axis];
					let relativeValue;

					if(axis === "r") {
						convertedCoordinates[axis] = axisCoordinate;
						continue;
					}

					//Restricting the value to go beyond the allowed values;
					axisCoordinate = axisCoordinate || 0;
					if(axisCoordinate > max) axisCoordinate = max;

					//Get the relative value for the given coordinate
					relativeValue = (axisCoordinate / max);

					//Specific axis handling
					if(axis === "x") {
						if(buffer?.width) {
							//Adding buffer so the Gantry UI Element doesn't go outside the play area
							relativeValue = relativeValue * (playAreaWidth - buffer.width);
						} else {
							relativeValue = relativeValue * playAreaWidth;
						}
					};

					if(axis === "y") {
						if(buffer?.height) {
							//Adding buffer so the Gantry UI Element doesn't go outside the play area
							relativeValue = (playAreaHeight - buffer.height) - relativeValue * (playAreaHeight - buffer.height);
						} else {
							relativeValue = playAreaHeight - (relativeValue * playAreaHeight)
						}
					};

					convertedCoordinates[axis] = Math.max(relativeValue, 0);

				} else {
					convertedCoordinates[axis] = Math.max(axisCoordinate, 0);
				}
			}

			return convertedCoordinates;

		case Views.SIDE_VIEW :
			for (const axis in newCoordinates) {
				let axisCoordinate = newCoordinates[axis];
				//Only handle the axes which have known dimensions
				if (axis in machineCoordinates) {
					const max = machineCoordinates[axis];
					let relativeValue;

					//Restricting the value to go beyond the allowed values;
					axisCoordinate = axisCoordinate || 0;
					if(axisCoordinate > max) axisCoordinate = max;
					if(axis === "z") {
						//Get the relative value for the given coordinate
						relativeValue = (axisCoordinate / max);
						relativeValue = (relativeValue * (playAreaHeight - Z_BUFFER))
						convertedCoordinates[axis] = Math.max(relativeValue, 0);
					} else {
						convertedCoordinates[axis] = Math.max(axisCoordinate, 0);
					}
				} else {
					convertedCoordinates[axis] = Math.max(axisCoordinate, 0);
				}
			}
			
			return convertedCoordinates;
		
		default :
			return { x:0, y:0, z:0, r:0  }
	}
};

const GantryLocate = (props) => {

	const { machineStructure, moduleData } = props;
	const { machineSetup } = moduleData;

	const [gantryCoordinates, setGantryCoordinates] = useState();

	const renderView = (config, view) => {
		switch(view) {
			case Views.TOP_VIEW : 
				const cssClasses = StyleClasses[view];
				const frontRow = config.FRONT.map((item) => (
					<div className={cssClasses[item]}></div>
				));
				const backRow = config.BACK.map((item) => (
					<div className={cssClasses[item]}></div>
				));
				const pickerComponent = GantryComponents[view];
				return (
					<div className={cssClasses.MACHINE}>
						<div className={cssClasses.BACK}>{backRow}</div>
						<div className={cssClasses.PLAY_AREA}>
							{pickerComponent}
						</div>
						<div className={cssClasses.FRONT}>{frontRow}</div>
					</div>
				)

			case Views.SIDE_VIEW :
				let classes = StyleClasses[view];
				let picker = GantryComponents[view];
				return (
					<div className={classes.MACHINE}>
						{picker}
					</div>
				)

			default :
				break;
		}
	};

	const handleNewPosition = (machineStructure, gantryCoordinates) => {
		const { machineCoordinates } = machineStructure;
		for (const view in Views) {
			const position = nextPosition(machineCoordinates, gantryCoordinates , view);
			moveGantry(position, view);
		}
	};

	const moveGantry = (position, view) => {
		const { x, y, z, r } = position;
		const element = document.getElementById(`${view}${GantrySuffix}`);
		if (!element) return;
		switch(view) {
			case Views.TOP_VIEW : 
				console.log(element, position)
				element.style.transform = `translate3d(${x}px, ${y}px, 0px) rotate(-${r}deg)`;
				break;

			case Views.SIDE_VIEW :
				element.style.transform = `translate(75px, -${z}px)`
				break

			default:
				break;
		}
	};

	const nextPosition = (machineConfig, newCoordinates, view) => {
		if (!(view in Views) || !newCoordinates) return;
		let position = {};
		const wrapperElement = document.getElementsByClassName(StyleClasses[view].PLAY_AREA)[0];
		newCoordinates = convertToUiCoordinates(newCoordinates, view, machineConfig, wrapperElement);
		switch (view) {
			case Views.TOP_VIEW:
				console.log(newCoordinates, view);
				position = { ...newCoordinates };
				break;

			case Views.SIDE_VIEW:
				position = { ...newCoordinates };
				break;

			default:
				break;
		}
		return position;
	};

	useLayoutEffect(() => {
		const { gantryCoordinates:newGantryCoordinates } = moduleData;
		setGantryCoordinates(newGantryCoordinates);
		console.log(newGantryCoordinates)
	}, [moduleData]);

	useLayoutEffect(() => {
		if(gantryCoordinates) {
			handleNewPosition(machineStructure, gantryCoordinates);
		}
	},[gantryCoordinates])

	return (
		<Page>
			{renderView(machineSetup, Views.TOP_VIEW)}
			<Divider />
			{renderView(machineSetup, Views.SIDE_VIEW)}
		</Page>
	);
};

export default GantryLocate;
